import {
  createContext,
  useContext,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Check, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InlineHelp } from '@/features/help';
import { cn } from '@/lib/utils';
import type { EntryStep } from '../lib/document-entry';

export type { EntryStep } from '../lib/document-entry';
const STEPS: readonly EntryStep[] = ['customer', 'items', 'review'];
const EntryContext = createContext({ guided: false, step: 'customer' as EntryStep });

export function DocumentEntryDetails({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  const { guided } = useContext(EntryContext);
  const [open, setOpen] = useState(!guided);
  return (
    <details
      className={cn('rounded-md border bg-card p-3', className)}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="min-h-11 cursor-pointer py-2 text-sm font-medium">{label}</summary>
      {children}
    </details>
  );
}

export function DocumentEntrySection({ step, children }: { step: EntryStep; children: ReactNode }) {
  const entry = useContext(EntryContext);
  const hidden = entry.guided && entry.step !== step;
  return (
    <section hidden={hidden} inert={hidden} className={hidden ? 'hidden' : 'space-y-6'}>
      {children}
    </section>
  );
}

export function DocumentEntryFlow({
  guided,
  step,
  onStepChange,
  onNext,
  onSave,
  onCancel,
  saving,
  saveDisabled,
  readOnly,
  isNew,
  children,
}: {
  guided: boolean;
  step: EntryStep;
  onStepChange: (step: EntryStep) => void;
  onNext: () => void;
  onSave: () => void;
  onCancel?: () => void;
  saving: boolean;
  saveDisabled: boolean;
  readOnly: boolean;
  isNew: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const previousStep = useRef(step);
  useLayoutEffect(() => {
    if (guided && previousStep.current !== step) heading.current?.focus();
    previousStep.current = step;
  }, [guided, step]);
  const index = STEPS.indexOf(step);
  return (
    <EntryContext.Provider value={{ guided, step }}>
      <div className="space-y-5 document-entry-flow">
        {guided && (
          <>
            <nav aria-label={t('documentEntry.stepsLabel')}>
              <ol className="document-entry-steps grid grid-cols-3 gap-2">
                {STEPS.map((entry, position) => (
                  <li key={entry}>
                    <button
                      type="button"
                      aria-current={entry === step ? 'step' : undefined}
                      className={cn(
                        'flex min-h-11 w-full items-center gap-2 rounded-md border px-2 py-3 text-left text-xs sm:px-3 sm:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        entry === step
                          ? 'border-primary bg-primary/10 font-semibold text-primary'
                          : 'border-border bg-card text-muted-foreground',
                      )}
                      onClick={() => onStepChange(entry)}
                    >
                      <span
                        className="hidden h-6 w-6 shrink-0 sm:flex items-center justify-center rounded-full border"
                        aria-hidden="true"
                      >
                        {position + 1}
                      </span>
                      <span>{t(`documentEntry.steps.${entry}`)}</span>
                    </button>
                  </li>
                ))}
              </ol>
            </nav>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2
                  ref={heading}
                  id={headingId}
                  tabIndex={-1}
                  className="text-xl font-semibold tracking-tight focus:outline-none"
                >
                  {t(`documentEntry.steps.${step}`)}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t(
                    `documentEntry.hints.${step === 'review' && !isNew ? 'reviewExisting' : step}`,
                  )}
                </p>
              </div>
              <InlineHelp articleId="documents.entry" label={t('documentEntry.help')} />
            </div>
          </>
        )}
        {children}
        {guided && (
          <footer
            className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-md border bg-background p-3"
            aria-labelledby={headingId}
          >
            <div className="flex items-center gap-2">
              {onCancel && (
                <Button type="button" variant="ghost" disabled={saving} onClick={onCancel}>
                  {t(readOnly ? 'common.close' : 'common.cancel')}
                </Button>
              )}
              {index > 0 && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => onStepChange(STEPS[index - 1] ?? 'customer')}
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  {t('documentEntry.back')}
                </Button>
              )}
              <span className="text-xs text-muted-foreground">{t('documentEntry.saveHint')}</span>
            </div>
            {step !== 'review' ? (
              <Button type="button" onClick={onNext} disabled={saving}>
                {t('documentEntry.next')}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            ) : (
              !readOnly && (
                <Button type="button" onClick={onSave} disabled={saveDisabled}>
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : isNew ? (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Save className="h-4 w-4" aria-hidden="true" />
                  )}
                  {t(isNew ? 'documentEntry.saveDraft' : 'common.save')}
                </Button>
              )
            )}
          </footer>
        )}
      </div>
    </EntryContext.Provider>
  );
}
